/**
 * Hausje search — the visible field is a normal search form.
 * Header search focuses that field. Suggestions stay in a dropdown.
 * The full-screen predictive modal is not the primary search control.
 */

import { hausjeReturnRecord, hausjeSearchDestination, hausjeVisibleTitle, refineSearchProducts } from '@theme/hausje-search-query';

const MODAL_ID = 'search-modal';
const VIEW_ALL_DEFAULT = 'Bekijk alle resultaten';
const SUGGEST_LIMIT = 6;
const SUGGEST_FETCH_LIMIT = 20;
const RETURN_KEY = 'hausje-return';

/**
 * @param {Element | null} el
 */
function isShown(el) {
  if (!(el instanceof HTMLElement)) return false;
  if (el.closest('[hidden]')) return false;
  const style = getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden' && el.getClientRects().length > 0;
}

/**
 * Visible page search field, excluding the closed header fallback panel.
 * @returns {HTMLInputElement | null}
 */
function pageSearchInput() {
  for (const input of document.querySelectorAll('.hausje-search-field__input')) {
    if (!(input instanceof HTMLInputElement)) continue;
    if (input.closest('[data-hausje-header-search-panel]')) continue;
    if (isShown(input)) return input;
  }
  return null;
}

/**
 * @param {HTMLElement | null} [except]
 */
function closeHeaderPanels(except = null) {
  document.querySelectorAll('[data-hausje-header-search-panel]').forEach((panel) => {
    if (!(panel instanceof HTMLElement) || panel === except) return;
    panel.hidden = true;
  });
}

/**
 * @param {HTMLInputElement} input
 */
function focusInput(input) {
  const rect = input.getBoundingClientRect();
  const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
  if (!inView) input.scrollIntoView({ block: 'nearest' });
  input.focus();
}

/**
 * @param {HTMLButtonElement} button
 */
function openHeaderPanel(button) {
  const root = button.closest('search-button');
  const panel = root?.querySelector('[data-hausje-header-search-panel]');
  if (!(panel instanceof HTMLElement)) return false;
  closeHeaderPanels(panel);
  panel.hidden = false;
  const input = panel.querySelector('input[name="q"]');
  if (input instanceof HTMLInputElement) focusInput(input);
  return true;
}

/**
 * @param {string} [seedQuery]
 */
function openHausjeSearch(seedQuery = '') {
  const query = String(seedQuery || '').trim();
  const pageInput = pageSearchInput();
  const input = pageInput || null;

  try {
    document.dispatchEvent(
      new CustomEvent('hausje:search-open', {
        bubbles: true,
        detail: {
          query,
          context: 'search_entry',
        },
      })
    );
  } catch {
    // Ignore analytics dispatch failures.
  }

  if (input) {
    closeHeaderPanels();
    if (query) input.value = query;
    focusInput(input);
    if (query) {
      input.dispatchEvent(
        new InputEvent('input', {
          bubbles: true,
          inputType: 'insertText',
          data: query,
        })
      );
    }
    return true;
  }

  const button = [...document.querySelectorAll('[data-hausje-header-search]')].find(
    (candidate) => candidate instanceof HTMLButtonElement && isShown(candidate)
  );
  if (!(button instanceof HTMLButtonElement)) return false;
  const opened = openHeaderPanel(button);
  if (!opened || !query) return opened;
  const panelInput = button.closest('search-button')?.querySelector('input[name="q"]');
  if (panelInput instanceof HTMLInputElement) {
    panelInput.value = query;
    panelInput.dispatchEvent(
      new InputEvent('input', {
        bubbles: true,
        inputType: 'insertText',
        data: query,
      })
    );
  }
  return true;
}

function bindHeaderSearch() {
  if (window.__hausjeHeaderSearchBound) return;
  window.__hausjeHeaderSearchBound = true;

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-hausje-header-search]');
    if (button instanceof HTMLButtonElement) {
      event.preventDefault();
      const pageInput = pageSearchInput();
      if (pageInput) {
        closeHeaderPanels();
        focusInput(pageInput);
        return;
      }
      openHeaderPanel(button);
      return;
    }
    if (target.closest('[data-hausje-header-search-panel]')) return;
    closeHeaderPanels();
  });

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape') {
        closeHeaderPanels();
        return;
      }
      if (event.metaKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        event.stopPropagation();
        openHausjeSearch();
      }
    },
    true
  );
}

/**
 * @param {string} url
 */
function safeProductPath(url) {
  if (!url.startsWith('/') || url.startsWith('//')) return '';
  try {
    const parsed = new URL(url, location.origin);
    if (parsed.origin !== location.origin) return '';
    if (!parsed.pathname.startsWith('/products/')) return '';
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return '';
  }
}

/**
 * Published collection products only. Drafts are not in products.json.
 * @param {string} destination
 * @param {AbortSignal} signal
 */
async function collectionSuggestions(destination, signal) {
  const path = destination.split('?')[0];
  const response = await fetch(`${path}/products.json?limit=${SUGGEST_LIMIT}`, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return [];
  const data = await response.json();
  return (data.products || [])
    .map((item) => ({
      title: String(item?.title || '').trim(),
      type: String(item?.product_type || '').trim(),
      vendor: String(item?.vendor || '').trim(),
      url: item?.handle ? `/products/${item.handle}` : '',
    }))
    .filter((item) => item.title && item.url)
    .slice(0, SUGGEST_LIMIT);
}

function rememberProductReturn() {
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a[href*="/products/"]');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (location.pathname.startsWith('/products/')) return;
    const record = hausjeReturnRecord(location.pathname, location.search, window.scrollY);
    try {
      sessionStorage.setItem(RETURN_KEY, JSON.stringify(record));
    } catch {
      // Private browsing can block storage. The breadcrumb keeps its fallback.
    }
  }, true);
}

function applyProductReturn() {
  let record = null;
  try {
    record = JSON.parse(sessionStorage.getItem(RETURN_KEY) || 'null');
  } catch {
    record = null;
  }
  if (!record?.url || !record.url.startsWith('/')) return;
  const recordPath = record.url.split('?')[0];
  document.querySelectorAll('[data-hausje-return]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const base = link.getAttribute('data-hausje-return-base') || '';
    if (base && recordPath !== base) return;
    link.href = record.url;
  });
  if (`${location.pathname}${location.search}` !== record.url) return;
  requestAnimationFrame(() => window.scrollTo(0, record.y || 0));
}

function bindInlinePredictive() {
  document.querySelectorAll('.hausje-search').forEach((wrap) => {
    if (!(wrap instanceof HTMLElement)) return;
    if (wrap.dataset.hausjeSuggestBound === 'true') return;
    wrap.dataset.hausjeSuggestBound = 'true';

    const input = wrap.querySelector('input[name="q"]');
    const list = wrap.querySelector('.hausje-search__suggest');
    if (!(input instanceof HTMLInputElement) || !(list instanceof HTMLElement)) return;

    let active = -1;
    let timer = 0;
    /** @type {AbortController | null} */
    let controller = null;

    const close = () => {
      active = -1;
      list.hidden = true;
      list.replaceChildren();
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      wrap.classList.remove('hausje-search--open');
    };

    /**
     * @param {{ title: string, vendor: string, url: string }[]} products
     * @param {string} query
     */
    const render = (products, query) => {
      list.replaceChildren();
      active = -1;
      input.removeAttribute('aria-activedescendant');
      if (!products.length) {
        close();
        return;
      }

      products.forEach((product, index) => {
        const link = document.createElement('a');
        link.className = 'hausje-search__suggest-link';
        link.href = product.url;
        link.setAttribute('role', 'option');
        link.id = `${input.id}-suggest-${index}`;
        const title = document.createElement('span');
        title.className = 'hausje-search__suggest-title';
        title.textContent = hausjeVisibleTitle(product.title);
        link.append(title);
        if (product.vendor) {
          const vendor = document.createElement('span');
          vendor.className = 'hausje-search__suggest-vendor';
          vendor.textContent = product.vendor;
          link.append(vendor);
        }
        list.append(link);
      });

      const all = document.createElement('a');
      all.className = 'hausje-search__suggest-all';
      const destination = hausjeSearchDestination(query);
      all.href = destination || `/search?type=product&q=${encodeURIComponent(query)}`;
      all.textContent = 'Bekijk alle resultaten';
      list.append(all);
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      wrap.classList.add('hausje-search--open');
    };

    const options = () => [...list.querySelectorAll('[role="option"]')];

    /**
     * @param {number} index
     */
    const setActive = (index) => {
      const items = options();
      if (!items.length) return;
      active = (index + items.length) % items.length;
      items.forEach((item, itemIndex) => {
        if (itemIndex === active) item.setAttribute('aria-selected', 'true');
        else item.removeAttribute('aria-selected');
      });
      const current = items[active];
      if (current instanceof HTMLElement) input.setAttribute('aria-activedescendant', current.id);
    };

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const query = input.value.trim();
      if (!query) {
        controller?.abort();
        close();
        return;
      }

      timer = window.setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        try {
          const url = new URL('/search/suggest.json', location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[limit]', String(SUGGEST_FETCH_LIMIT));
          url.searchParams.set('resources[options][unavailable_products]', 'last');
          const response = await fetch(url, {
            signal: controller.signal,
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) {
            close();
            return;
          }
          const data = await response.json();
          const raw = data?.resources?.results?.products;
          const mapped = Array.isArray(raw)
            ? raw
                .map((item) => ({
                  title: String(item?.title || '').trim(),
                  type: String(item?.type || '').trim(),
                  vendor: String(item?.vendor || '').trim(),
                  url: safeProductPath(String(item?.url || '')),
                }))
                .filter((item) => item.title && item.url)
            : [];
          let products = refineSearchProducts(query, mapped).slice(0, SUGGEST_LIMIT);
          if (!products.length) {
            const destination = hausjeSearchDestination(query);
            if (destination.startsWith('/collections/')) products = await collectionSuggestions(destination, controller.signal);
          }
          if (input.value.trim() !== query) return;
          render(products, query);
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          close();
        }
      }, 200);
    });

    input.addEventListener('keydown', (event) => {
      const items = options();
      if (event.key === 'Escape') {
        if (!list.hidden) {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
        return;
      }
      if (list.hidden || !items.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive(active + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(active < 0 ? items.length - 1 : active - 1);
      } else if (event.key === 'Enter' && active >= 0) {
        event.preventDefault();
        const current = items[active];
        if (current instanceof HTMLAnchorElement) current.click();
      }
    });
  });
}

/**
 * @param {HTMLElement} modal
 * @param {string} query
 */
function updateViewAllLabel(modal, query) {
  const button = modal.querySelector('.predictive-search__search-button');
  if (!(button instanceof HTMLElement)) return;
  const q = String(query || '').trim();
  button.textContent = q ? `Bekijk alle resultaten voor “${q}”` : VIEW_ALL_DEFAULT;
}

function syncHeroPlaceholders() {
  const mobile = window.matchMedia('(max-width: 749px)').matches;
  document.querySelectorAll('[data-hausje-search-entry]').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    const input = form.querySelector('input[type="search"], input[name="q"]');
    if (!(input instanceof HTMLInputElement)) return;
    const desktop = form.getAttribute('data-placeholder-desktop') || '';
    const mobileText = form.getAttribute('data-placeholder-mobile') || desktop;
    const next = mobile ? mobileText : desktop;
    if (next && input.placeholder !== next) {
      input.placeholder = next;
    }
  });
}

function bindHeroSearchFields() {
  // The visible field is a normal search form: focus, type, Enter → /search.
  // Header search still opens the predictive modal. Do not steal this field.
  document.querySelectorAll('[data-hausje-search-entry]').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    form.dataset.hausjeSearchBound = 'native';
  });

  syncHeroPlaceholders();
  if (!window.__hausjeSearchPlaceholderBound) {
    window.__hausjeSearchPlaceholderBound = true;
    window.matchMedia('(max-width: 749px)').addEventListener('change', syncHeroPlaceholders);
  }
}

function bindModalViewAllLabel() {
  const modal = document.getElementById(MODAL_ID);
  if (!(modal instanceof HTMLElement)) return;

  const input = modal.querySelector('input.search-input');
  if (!(input instanceof HTMLInputElement)) return;

  input.addEventListener('input', () => {
    updateViewAllLabel(modal, input.value);
  });
}

/**
 * Privacy-safe inspiration room click (homepage CTA / image).
 */
function bindInspirationClicks() {
  if (window.__hausjeInspirationClickBound) return;
  window.__hausjeInspirationClickBound = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest('[data-hausje-inspiration-link]');
      if (!(link instanceof HTMLAnchorElement)) return;
      const root = link.closest('[data-hausje-inspiration]');
      if (!(root instanceof HTMLElement)) return;
      try {
        document.dispatchEvent(
          new CustomEvent('hausje:inspiration-click', {
            bubbles: true,
            detail: {
              handle: root.getAttribute('data-hausje-inspiration-product-handle') || '',
              product_id: root.getAttribute('data-hausje-inspiration-product-id') || '',
              category: root.getAttribute('data-hausje-inspiration-category') || '',
              placement: 'homepage',
            },
          })
        );
      } catch {
        // Ignore analytics dispatch failures.
      }
    },
    { capture: true }
  );
}

/**
 * Privacy-safe product click signal for discovery cards (no third-party pixels).
 */
function bindDiscoveryProductClicks() {
  if (window.__hausjeDiscoveryClickBound) return;
  window.__hausjeDiscoveryClickBound = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest(
        '[data-hausje-discovery-card] a.hausje-discovery-card__media-link, [data-hausje-discovery-card] a.hausje-discovery-card__title'
      );
      if (!(link instanceof HTMLAnchorElement)) return;
      const card = link.closest('[data-hausje-discovery-card]');
      if (!(card instanceof HTMLElement)) return;
      try {
        document.dispatchEvent(
          new CustomEvent('hausje:product-click', {
            bubbles: true,
            detail: {
              handle: card.getAttribute('data-product-handle') || '',
              product_id: card.getAttribute('data-product-id') || '',
              context: card.getAttribute('data-product-view-context') || 'discovery',
            },
          })
        );
      } catch {
        // Ignore analytics dispatch failures.
      }
    },
    { capture: true }
  );
}

function bindSearchDestination() {
  if (window.__hausjeSearchDestinationBound) return;
  window.__hausjeSearchDestinationBound = true;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.getAttribute('role') !== 'search') return;
      const input = form.querySelector('input[name="q"]');
      if (!(input instanceof HTMLInputElement)) return;
      const destination = hausjeSearchDestination(input.value);
      if (!destination) return;
      event.preventDefault();
      window.location.assign(destination);
    },
    true
  );
}

function prefillSearchTerm() {
  const term = new URLSearchParams(window.location.search).get('q');
  if (!term) return;
  document.querySelectorAll('input[name="q"]').forEach((input) => {
    if (input instanceof HTMLInputElement && !input.value) input.value = term;
  });
}

function init() {
  bindHeroSearchFields();
  bindHeaderSearch();
  bindInlinePredictive();
  bindModalViewAllLabel();
  bindSearchDestination();
  prefillSearchTerm();
  bindDiscoveryProductClicks();
  bindInspirationClicks();
  rememberProductReturn();
  applyProductReturn();
}

window.HausjeSearch = {
  open: openHausjeSearch,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
